// Facebook Messenger inbox.
//
// Same shape as the WhatsApp inbox on purpose - two panes, the conversations
// and the one you are in - so an agent moving between channels is not learning
// a second screen. The differences are Messenger's, not ours:
//
//   · a person is a PSID (a page-scoped id), not a phone number
//   · there is no "start a new conversation": Messenger only lets you reply to
//     someone who has written to the Page first, so there is no compose button
//   · no templates - Messenger's equivalent is message tags, which this channel
//     does not use yet
//
// The 24-hour standard messaging window still applies, so it is shown on every
// row and enforced on the composer.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, MessageCircle, RefreshCw, Send } from 'lucide-react';
import { useToast } from '@centraoneplus/ui';
import { useInboxRealtime } from '@/hooks/useInboxRealtime';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import {
  facebookService,
  type FacebookCannedReply,
  type FacebookConversation,
  type FacebookConversationThread,
  type FacebookTimelineMessage,
} from '@/services/social-channels.service';

/** The floating shell: cards sit ON the canvas, they do not butt against it. */
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-[10px] border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY =
  'flex h-[34px] flex-none items-center gap-[7px] rounded-lg bg-[var(--co-brass-ink)] px-[14px] ' +
  'text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

// The socket accelerates this; it never replaces it. A connected socket does
// not prove events are flowing (the BFF relay is optional and disables itself
// without a Kafka broker), so the poll stays at one fixed, safe cadence.
const POLL_MS = 15_000;

function clock(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function dayLabel(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const isToday = d.toDateString() === new Date().toDateString();
  return isToday ? clock(iso) : d.toLocaleDateString([], { day: '2-digit', month: 'short' });
}

/** Initials for the avatar; a PSID is opaque, so fall back to a glyph. */
function initials(name: string | null): string {
  const source = (name || '').trim();
  if (!source) return '?';
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

/** A PSID is a long opaque number; showing all of it helps nobody. */
function participantLabel(conversation: { name: string | null; participantId: string }): string {
  return conversation.name || `Messenger user ${conversation.participantId.slice(-6)}`;
}

function sameDay(a: string, b: string): boolean {
  const x = new Date(a);
  const y = new Date(b);
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  );
}

function daySeparatorLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Minutes left on Messenger's 24-hour window, or null once it has closed. */
function windowRemaining(expiresAt: string | null): string | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  return hours > 0 ? `${hours}h ${minutes}m left` : `${minutes}m left`;
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'Sending…',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed: 'Failed',
};

export default function FacebookInboxPage() {
  const toast = useToast();

  const [conversations, setConversations] = useState<FacebookConversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [thread, setThread] = useState<FacebookConversationThread | null>(null);
  const [canned, setCanned] = useState<FacebookCannedReply[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showCanned, setShowCanned] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const endRef = useRef<HTMLDivElement>(null);
  const cannedRef = useRef<HTMLDivElement>(null);

  const loadConversations = useCallback(async () => {
    try {
      const rows = await facebookService.listConversations();
      setConversations(rows);
      setError(null);
      return rows;
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          'Could not load Messenger conversations. Check the channel is configured in Settings.',
      );
      return [];
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const [rows] = await Promise.all([
        loadConversations(),
        facebookService
          .getAutoReply()
          .then((settings) => {
            if (!cancelled) setCanned(settings.cannedReplies);
          })
          .catch(() => {
            // Canned replies are a convenience; the inbox works without them.
          }),
      ]);
      if (!cancelled) {
        setIsLoading(false);
        if (rows.length > 0) setSelectedId((current) => current ?? rows[0].participantId);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadConversations]);

  const loadThread = useCallback(
    async (participantId: string, { markRead = false } = {}) => {
      setIsLoadingThread(true);
      try {
        const data = await facebookService.getConversation(participantId);
        setThread(data);
        if (markRead) {
          // Shows "Seen" to the person, and clears the unread badge here.
          // Best effort: a failed receipt must not block reading the thread.
          facebookService
            .markConversationRead(participantId)
            .then(() => loadConversations())
            .catch(() => undefined);
        }
      } catch (err: any) {
        toast.error(
          'Could not open the conversation',
          err.response?.data?.message || 'Please try again.',
        );
      } finally {
        setIsLoadingThread(false);
      }
    },
    [toast, loadConversations],
  );

  useEffect(() => {
    if (selectedId) void loadThread(selectedId, { markRead: true });
  }, [selectedId, loadThread]);

  // A customer's message reaches us by webhook; this is how it reaches the
  // screen without waiting out a poll. Refetch rather than render from the
  // event: it is broadcast to the whole tenant room and carries no content.
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const handleInboundMessage = useCallback(() => {
    void loadConversations();
    const open = selectedIdRef.current;
    if (open) void loadThread(open);
  }, [loadConversations, loadThread]);

  const { isLive } = useInboxRealtime({
    channel: 'facebook',
    onMessage: handleInboundMessage,
  });

  // The list still polls, slowly while the socket is live.
  useEffect(() => {
    const timer = setInterval(() => void loadConversations(), POLL_MS);
    return () => clearInterval(timer);
  }, [loadConversations]);

  // So does the open thread.
  useEffect(() => {
    if (!selectedId) return undefined;
    const timer = setInterval(() => void loadThread(selectedId), POLL_MS);
    return () => clearInterval(timer);
  }, [selectedId, loadThread]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.messages.length]);

  useEffect(() => {
    if (!showCanned) return undefined;
    const onClick = (event: MouseEvent) => {
      if (cannedRef.current && !cannedRef.current.contains(event.target as Node)) {
        setShowCanned(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showCanned]);

  const selected = useMemo(
    () => conversations.find((c) => c.participantId === selectedId) || null,
    [conversations, selectedId],
  );

  const windowOpen = thread?.isWithin24h ?? selected?.isWithin24h ?? false;
  const remaining = windowRemaining(
    thread?.windowExpiresAt ?? selected?.windowExpiresAt ?? null,
  );

  const handleSend = async () => {
    const message = draft.trim();
    if (!message || !selectedId || isSending) return;

    setIsSending(true);
    try {
      const result = await facebookService.sendTextMessage({
        recipientId: selectedId,
        message,
      });
      if (!result.success) {
        // A Meta-side rejection comes back as success:false, not an HTTP error.
        toast.error('Messenger rejected the message', result.message);
      } else {
        setDraft('');
      }
      await Promise.all([loadThread(selectedId), loadConversations()]);
    } catch (err: any) {
      toast.error('Could not send', err.response?.data?.message || 'Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return (
    <div className="flex h-full min-h-0 flex-col gap-[14px] overflow-hidden bg-[var(--co-canvas)] py-4 pr-4">
      <header
        className={clsx(CARD_CLASS, 'flex-none px-5 py-[18px]')}
        style={{ ...CARD, borderRadius: 14 }}
      >
        <div className="flex items-center justify-between gap-[10px]">
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
              Facebook
            </h1>
            <p className="co-mono mt-[6px] flex items-center gap-[7px] text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
              <span>
                {conversations.length} conversation{conversations.length === 1 ? '' : 's'}
                {totalUnread > 0 ? ` · ${totalUnread} unread` : ''}
              </span>
              {/* Whether new messages are being pushed or merely polled for. */}
              <span
                className="flex items-center gap-[4px]"
                title={
                  isLive
                    ? 'Connected - new messages appear as they arrive'
                    : 'Not connected - checking for new messages periodically'
                }
              >
                <span
                  aria-hidden
                  className={clsx(
                    'h-[6px] w-[6px] rounded-full',
                    isLive ? 'bg-[var(--co-green)]' : 'bg-[var(--co-ink-4)]',
                  )}
                />
                {isLive ? 'Live' : 'Polling'}
              </span>
            </p>
          </div>
          <div className="flex h-[34px] flex-none items-center gap-2">
            <button
              type="button"
              className={CONTROL}
              onClick={() => {
                void loadConversations();
                if (selectedId) void loadThread(selectedId);
              }}
            >
              <RefreshCw className="h-[13px] w-[13px]" />
              Refresh
            </button>
            <PageHeaderMeta />
          </div>
        </div>
      </header>

      {error && (
        <div
          className={clsx(CARD_CLASS, 'flex flex-none items-center gap-2 px-4 py-3')}
          style={{ ...CARD, borderColor: 'var(--co-terracotta)' }}
        >
          <AlertTriangle className="h-4 w-4 flex-none text-[var(--co-terracotta)]" />
          <span className="text-[12.5px] text-[var(--co-ink-2)]">{error}</span>
        </div>
      )}

      <div className="flex min-h-0 flex-1 gap-[14px]">
        {/* conversation list */}
        <aside
          className={clsx(CARD_CLASS, 'flex w-[320px] flex-none flex-col overflow-hidden')}
          style={CARD}
        >
          <div className="flex-none border-b border-[var(--co-line)] px-4 py-3">
            <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
              Conversations
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading && (
              <p className="px-4 py-6 text-[12.5px] text-[var(--co-ink-3)]">Loading…</p>
            )}

            {!isLoading && conversations.length === 0 && (
              <div className="px-4 py-8 text-center">
                <MessageCircle className="mx-auto h-6 w-6 text-[var(--co-ink-3)]" />
                <p className="mt-3 text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
                  No conversations yet. They appear here the moment someone messages your
                  Page.
                </p>
              </div>
            )}

            {conversations.map((conversation) => {
              const isSelected = conversation.participantId === selectedId;
              return (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => setSelectedId(conversation.participantId)}
                  className="relative flex w-full items-start gap-3 px-4 py-[13px] text-left transition-colors"
                  style={{
                    background: isSelected ? 'var(--co-selected)' : 'transparent',
                    boxShadow: 'inset 0 -1px 0 var(--co-line-subtle)',
                  }}
                >
                  {isSelected && (
                    <span
                      className="absolute inset-y-0 left-0 w-[3px]"
                      style={{ background: 'var(--co-brass-ink)' }}
                      aria-hidden
                    />
                  )}

                  <span
                    className="mt-[1px] flex h-9 w-9 flex-none items-center justify-center rounded-full text-[12px] font-bold"
                    style={{
                      background: 'var(--co-brass-wash)',
                      color: 'var(--co-brass-ink)',
                    }}
                    aria-hidden
                  >
                    {initials(conversation.name)}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-[13px] font-semibold text-[var(--co-ink)]">
                        {participantLabel(conversation)}
                      </span>
                      <span className="co-mono flex-none text-[10px] text-[var(--co-ink-3)]">
                        {dayLabel(conversation.lastMessageAt)}
                      </span>
                    </span>

                    <span className="mt-[3px] flex items-center justify-between gap-2">
                      <span
                        className="truncate text-[12px]"
                        style={{
                          color:
                            conversation.unreadCount > 0
                              ? 'var(--co-ink-2)'
                              : 'var(--co-ink-3)',
                          fontWeight: conversation.unreadCount > 0 ? 600 : 400,
                        }}
                      >
                        {conversation.lastDirection === 'outbound' ? 'You: ' : ''}
                        {conversation.lastMessage || 'No text content'}
                      </span>
                      {conversation.unreadCount > 0 && (
                        <span
                          className="flex-none rounded-full px-[6px] py-[1px] text-[10px] font-bold leading-[1.5]"
                          style={{
                            background: 'var(--co-brass-ink)',
                            color: 'var(--co-brass-on)',
                          }}
                        >
                          {conversation.unreadCount}
                        </span>
                      )}
                    </span>

                    {!conversation.isWithin24h && (
                      <span className="co-mono mt-[5px] inline-block text-[9px] uppercase tracking-[.14em] text-[var(--co-ink-3)]">
                        Window closed
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* thread */}
        <section
          className={clsx(CARD_CLASS, 'flex min-w-0 flex-1 flex-col overflow-hidden')}
          style={CARD}
        >
          {!selectedId && (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-[12.5px] text-[var(--co-ink-3)]">
                Pick a conversation to read it.
              </p>
            </div>
          )}

          {selectedId && (
            <>
              <div className="flex flex-none items-center justify-between gap-3 border-b border-[var(--co-line)] px-5 py-3">
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-bold text-[var(--co-ink)]">
                    {thread?.name ||
                      selected?.name ||
                      `Messenger user ${selectedId.slice(-6)}`}
                  </p>
                  <p className="co-mono mt-[3px] text-[10px] uppercase tracking-[.14em] text-[var(--co-ink-3)]">
                    Messenger{remaining ? ` · ${remaining}` : ''}
                  </p>
                </div>
              </div>

              <div
                className="min-h-0 flex-1 overflow-y-auto px-5 py-4"
                style={{ background: 'var(--co-sunken)' }}
              >
                {isLoadingThread && !thread && (
                  <p className="text-[12.5px] text-[var(--co-ink-3)]">Loading…</p>
                )}

                {thread?.messages.map((message, index) => {
                  const previous = thread.messages[index - 1];
                  const newDay = !previous || !sameDay(previous.at, message.at);
                  const grouped = !newDay && previous?.direction === message.direction;

                  return (
                    <Fragment key={message.id}>
                      {newDay && (
                        <div className="my-4 flex justify-center">
                          <span
                            className="co-mono rounded-full px-[10px] py-[3px] text-[9.5px] uppercase tracking-[.14em]"
                            style={{
                              background: 'var(--co-raised)',
                              color: 'var(--co-ink-3)',
                              border: '1px solid var(--co-line-subtle)',
                            }}
                          >
                            {daySeparatorLabel(message.at)}
                          </span>
                        </div>
                      )}
                      <MessageBubble message={message} grouped={grouped} />
                    </Fragment>
                  );
                })}

                <div ref={endRef} />
              </div>

              {/* composer */}
              <div className="flex-none border-t border-[var(--co-line)] px-5 py-3">
                {!windowOpen && (
                  <div className="mb-2 flex items-start gap-2 rounded-lg border border-[var(--co-line)] px-3 py-2">
                    <AlertTriangle className="mt-[2px] h-[13px] w-[13px] flex-none text-[var(--co-terracotta)]" />
                    <p className="text-[11.5px] leading-[1.5] text-[var(--co-ink-3)]">
                      The 24-hour reply window has closed. Messenger only allows a plain
                      reply within 24 hours of the person&apos;s last message — wait for
                      them to write again.
                    </p>
                  </div>
                )}

                <div className="flex items-end gap-2">
                  <textarea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        void handleSend();
                      }
                    }}
                    rows={2}
                    disabled={!windowOpen}
                    placeholder={
                      windowOpen ? 'Write a reply…' : 'Replies are closed until they write again'
                    }
                    className="min-h-[46px] flex-1 resize-none rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 py-2 text-[12.5px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-3)] focus:border-[var(--co-brass)] focus:outline-none disabled:opacity-60"
                  />

                  <div className="relative flex-none" ref={cannedRef}>
                    <button
                      type="button"
                      className={CONTROL}
                      disabled={!windowOpen || canned.length === 0}
                      onClick={() => setShowCanned((open) => !open)}
                      title={
                        canned.length === 0
                          ? 'Add canned replies in Settings → Social Channels'
                          : 'Insert a canned reply'
                      }
                    >
                      Canned
                    </button>

                    {showCanned && canned.length > 0 && (
                      <div
                        className={clsx(CARD_CLASS, 'absolute bottom-[42px] right-0 w-[280px] p-1')}
                        style={{ borderRadius: 10, boxShadow: 'var(--co-shadow-pop)' }}
                      >
                        {canned.map((reply) => (
                          <button
                            key={reply.id}
                            type="button"
                            onClick={() => {
                              setDraft(reply.body);
                              setShowCanned(false);
                            }}
                            className="block w-full rounded-lg px-3 py-2 text-left hover:bg-[var(--co-brass-wash)]"
                          >
                            <span className="block text-[12.5px] font-semibold text-[var(--co-ink)]">
                              {reply.title}
                            </span>
                            <span className="mt-[2px] block truncate text-[11.5px] text-[var(--co-ink-3)]">
                              {reply.body}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className={PRIMARY}
                    disabled={!windowOpen || !draft.trim() || isSending}
                    onClick={() => void handleSend()}
                  >
                    <Send className="h-[13px] w-[13px]" />
                    {isSending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

/** One line of the transcript. Inbound left, outbound right. */
function MessageBubble({
  message,
  grouped = false,
}: {
  message: FacebookTimelineMessage;
  grouped?: boolean;
}) {
  const isOutbound = message.direction === 'outbound';
  const failed = message.status === 'failed';
  const hasAttachments = message.attachments.length > 0;

  return (
    <div
      className={clsx('flex', isOutbound ? 'justify-end' : 'justify-start')}
      style={{ marginTop: grouped ? 3 : 10 }}
    >
      <div
        className="max-w-[72%] px-[11px] py-[7px]"
        style={{
          background: isOutbound ? 'var(--co-brass-ink)' : 'var(--co-raised)',
          color: isOutbound ? 'var(--co-brass-on)' : 'var(--co-ink)',
          border: isOutbound ? 'none' : '1px solid var(--co-line-subtle)',
          borderRadius: isOutbound ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
          boxShadow: isOutbound ? 'none' : 'var(--co-shadow-card)',
          ...(failed ? { border: '1px solid var(--co-terracotta)' } : {}),
        }}
      >
        {/* Attachments are stored with the message but not fetched yet, so they
            are named rather than shown. */}
        {hasAttachments && (
          <p className="mb-1 text-[12px] italic opacity-80">
            [{message.attachments.length} {message.type} attachment
            {message.attachments.length === 1 ? '' : 's'}]
          </p>
        )}

        {message.text ? (
          <p className="whitespace-pre-wrap break-words text-[12.5px] leading-[1.5]">
            {message.text}
          </p>
        ) : (
          !hasAttachments && (
            <p className="text-[12.5px] italic opacity-70">[{message.type} message]</p>
          )
        )}

        <div
          className="mt-[3px] flex items-center justify-end gap-[6px] text-[9.5px] opacity-65"
          style={failed ? { color: 'var(--co-terracotta)', opacity: 1 } : undefined}
        >
          {message.automated && (
            <span className="co-mono uppercase tracking-[.14em]">Auto</span>
          )}
          <span className="co-mono">{clock(message.at)}</span>
          {isOutbound && message.status && (
            <span className="co-mono">{STATUS_LABEL[message.status] || message.status}</span>
          )}
        </div>

        {failed && message.errorMessage && (
          <p
            className="mt-[5px] rounded-[6px] px-[7px] py-[4px] text-[11px] leading-[1.4]"
            style={{
              background: 'var(--co-terracotta-wash)',
              color: 'var(--co-terracotta-ink)',
            }}
          >
            {message.errorMessage}
          </p>
        )}
      </div>
    </div>
  );
}
